import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import PropTypes from "prop-types";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { CustomersServices } from "./Customers.services";
import GROUPS_CONSTANTS, {
  GROUP_ACCESS_DRAWER_TAB_KEYS,
  GROUP_ACCESS_DRAWER_TABS,
  CUSTOMERS_PAGE_CONSTANTS,
} from "./Customers.constants";
import {
  SIDE_DRAWER_UI,
  sideDrawerBackdropSx,
  sideDrawerRootSx,
} from "common/Components/SideDrawer/SideDrawer.constants";
import {
  CancelButton,
  DrawerCloseButton,
  DrawerCloseIcon,
  DrawerFooter,
  DrawerHeader,
  DrawerShell,
  DrawerTitle,
  SubmitButton,
} from "common/Components/SideDrawer/SideDrawer.styles";
import CustomerDataConnectionsTab from "./CustomerDataConnectionsTab";
import CustomerEntitlementsTab from "./CustomerEntitlementsTab";
import StatusPill from "common/Components/StatusPill";
import {
  DrawerSplitLayout,
  DrawerSubtitle,
  DrawerTabPanel,
  DrawerTabsPane,
  DrawerTitleStatusWrap,
  customerDetailsDrawerPaperSx,
  drawerFooterSx,
  drawerTabSx,
  drawerTabsSx,
} from "./CustomerDetailsDrawer.styles";

const TAB_KEYS = GROUP_ACCESS_DRAWER_TAB_KEYS;
const DRAWER_TABS = GROUP_ACCESS_DRAWER_TABS;

const Toast = lazy(() => import("common/Components/Toast"));

function modulesFromApi(res) {
  if (res?.status !== 200) return null;
  const list = res?.data?.modules;
  if (!Array.isArray(list)) return [];
  return [...list];
}

function failureToast(description) {
  return {
    show: true,
    type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
    title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
    description:
      typeof description === "string" && description.trim()
        ? description
        : GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
  };
}

const CustomerDetailsDrawer = ({
  open,
  onClose,
  customerName,
  customerCode,
  isDeleted = false,
  modules: modulesProp,
  onDataConnectionsApply,
  dataConnectionsApplying = false,
  groups,
  onModulesUpdated,
  getAllCustomers,
}) => {
  const customersServices = useMemo(() => CustomersServices(), []);
  const [moduleRows, setModuleRows] = useState([]);
  const [patchInFlight, setPatchInFlight] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.DATA_CONNECTIONS);
  const [toast, setToast] = useState({
    show: false,
    type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
    title: "",
    description: "",
  });
  const [applyDisabled, setApplyDisabled] = useState(true);
  const dataConnectionsRef = useRef(null);
  const dismissToast = useCallback(() => {
    setToast((t) => ({ ...t, show: false }));
  }, []);

  useEffect(() => {
    if (!open) {
      setActiveTab(TAB_KEYS.DATA_CONNECTIONS);
      setApplyDisabled(true);
    }
  }, [open]);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      dismissToast();
    }
    prevOpenRef.current = open;
  }, [open, dismissToast]);

  useEffect(() => {
    if (!open) return undefined;
    if (modulesProp !== undefined) {
      setModuleRows(Array.isArray(modulesProp) ? [...modulesProp] : []);
      return undefined;
    }
    setModuleRows([]);
    return undefined;
  }, [open, modulesProp]);

  const handleDrawerClose = () => {
    dismissToast();
    onClose();
  };

  const handleToastClose = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const handleToggle = async (moduleKey, enabled) => {
    const snapshot = moduleRows;
    setModuleRows((rows) =>
      rows.map((r) => (r.module === moduleKey ? { ...r, enabled } : r))
    );
    dismissToast();

    const usePatchApi = Boolean(customerCode);
    if (!usePatchApi) {
      return;
    }

    setPatchInFlight(true);

    const patchRes = await customersServices.patchModuleAccess(
      moduleKey,
      { enabled },
      customerCode
    );

    const nextRows = modulesFromApi(patchRes);
    if (nextRows !== null) {
      setModuleRows(nextRows);
      onModulesUpdated?.(nextRows);
      getAllCustomers?.();
      setPatchInFlight(false);
      return;
    }

    if (patchRes.status === 200 || patchRes.status === 204) {
      setPatchInFlight(false);
      return;
    }

    setModuleRows(snapshot);
    setToast(failureToast(patchRes.error));
    setPatchInFlight(false);
  };

  const drawerTitle =
    customerName?.trim() !== "" ? `Customer: ${customerName}` : "Customer";
  const customerStatusLabel = isDeleted ? "Inactive" : "Active";
  const switchesDisabled = patchInFlight;
  const isDataConnectionsTab = activeTab === TAB_KEYS.DATA_CONNECTIONS;
  const handleApplyClick = () => {
    if (!isDataConnectionsTab) return;
    dataConnectionsRef.current?.apply?.();
  };
  const handleApplyStateChange = useCallback(({ disabled }) => {
    setApplyDisabled(Boolean(disabled));
  }, []);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        sx={sideDrawerRootSx}
        slotProps={{
          backdrop: { sx: sideDrawerBackdropSx },
        }}
        PaperProps={{ sx: customerDetailsDrawerPaperSx }}
        ModalProps={{ keepMounted: false }}
      >
        <DrawerShell>
          <DrawerHeader sx={{ alignItems: "flex-start" }}>
            <div>
              <DrawerTitle variant="h2" component="h2">
                {drawerTitle}
                <DrawerTitleStatusWrap>
                  <StatusPill
                    label={customerStatusLabel}
                    tone="ok"
                  />
                </DrawerTitleStatusWrap>
              </DrawerTitle>
              {customerCode ? (
                <DrawerSubtitle component="p">
                  {`Customer ID: ${customerCode}`}
                </DrawerSubtitle>
              ) : null}
            </div>
            <DrawerCloseButton
              aria-label={SIDE_DRAWER_UI.closeAriaLabel}
              onClick={handleDrawerClose}
              size="small"
            >
              <DrawerCloseIcon />
            </DrawerCloseButton>
          </DrawerHeader>

          <DrawerSplitLayout>
            <DrawerTabsPane>
              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                sx={drawerTabsSx}
              >
                {DRAWER_TABS?.map((tab) => (
                  <Tab
                    key={tab?.value}
                    value={tab?.value}
                    label={tab?.label}
                    sx={drawerTabSx}
                  />
                ))}
              </Tabs>
            </DrawerTabsPane>

            <DrawerTabPanel>
              {activeTab === TAB_KEYS.PAGE_ACCESS_CONTROL ? (
                <CustomerEntitlementsTab
                  loadingModules={false}
                  moduleRows={moduleRows}
                  switchesDisabled={switchesDisabled}
                  onToggle={handleToggle}
                />
              ) : (
                <CustomerDataConnectionsTab
                  ref={dataConnectionsRef}
                  onApply={onDataConnectionsApply}
                  applying={dataConnectionsApplying}
                  groups={groups}
                  onApplyStateChange={handleApplyStateChange}
                />
              )}
            </DrawerTabPanel>
          </DrawerSplitLayout>

          {isDataConnectionsTab && <DrawerFooter sx={drawerFooterSx}>
            <CancelButton
              type="button"
              onClick={handleDrawerClose}
              disabled={dataConnectionsApplying || patchInFlight}
            >
              {CUSTOMERS_PAGE_CONSTANTS.DETAIL_CANCEL_LABEL}
            </CancelButton>
            <SubmitButton
              type="button"
              variant="contained"
              onClick={handleApplyClick}
              disabled={
                !isDataConnectionsTab ||
                applyDisabled ||
                dataConnectionsApplying ||
                patchInFlight
              }
            >
              {CUSTOMERS_PAGE_CONSTANTS.DETAIL_APPLY_LABEL}
            </SubmitButton>
          </DrawerFooter>}
        </DrawerShell>
      </Drawer>

      <Suspense fallback={null}>
        <Toast
          type={toast.type}
          title={toast.title}
          description={toast.description}
          show={toast.show}
          onClose={handleToastClose}
          duration={GROUPS_CONSTANTS.toast.DURATION}
        />
      </Suspense>
    </>
  );
};

CustomerDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerName: PropTypes.string,
  customerCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isDeleted: PropTypes.bool,
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      module: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onDataConnectionsApply: PropTypes.func,
  dataConnectionsApplying: PropTypes.bool,
  groups: PropTypes.array,
  onModulesUpdated: PropTypes.func,
  getAllCustomers: PropTypes.func,
};

export default CustomerDetailsDrawer;
